<script setup>
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { reactive, ref } from 'vue';
import { boolean, object } from 'yup';

const { tenant, countries, seoTrackedKeyword, callback } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    countries: {
        type: Array,
        required: true,
    },
    seoTrackedKeyword: {
        type: Object,
        required: true,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const isOpen = ref(false);

const form = reactive({
    keyword: seoTrackedKeyword.keyword || '',
    isTracked: seoTrackedKeyword.isTracked || false,
    importantToFollow: seoTrackedKeyword.importantToFollow || false,
    country: seoTrackedKeyword.country
        ? countries.find(
              (country) => country.id === seoTrackedKeyword.country.id,
          )
        : null,
});

const validationSchema = object({
    isTracked: boolean(),
    importantToFollow: boolean(),
    country: object().nullable(),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.seo.postUpdateTrackedKeyword', {
                tenant: tenant.id,
                seoTrackedKeyword: seoTrackedKeyword.id,
            }),
            {
                isTracked: form.isTracked,
                importantToFollow: form.importantToFollow,
                countryId: form.country ? form.country.id : null,
            },
        )
        .then(() => {
            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Mettre à jour </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="1">
                            <InputField
                                v-model="form.keyword"
                                label="Mot-clé"
                                field-name="keyword"
                                field-id="keyword"
                                required
                                disabled
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="2">
                            <SwitchField
                                v-model="form.isTracked"
                                label="Suivi actif"
                                field-name="isTracked"
                                field-id="isTracked"
                            />

                            <SwitchField
                                v-model="form.importantToFollow"
                                label="Important à suivre"
                                field-name="importantToFollow"
                                field-id="importantToFollow"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <ComboboxField
                                v-model="form.country"
                                :options="countries"
                                field-name="country"
                                field-id="country"
                                label="Pays"
                                filtered-option-key="name"
                                has-empty-option
                            >
                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ComboboxField>
                        </FormGrid>
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
